import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Waves, Check, X, MapPin } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import FilmSection from "@/components/od/FilmSection";

const SITE = "https://oceandata.ca";

const MARQUEE = [
  "Moins de paperasse", "Moins de répétition", "Moins de courses",
  "Plus de contrôle", "Un seul système simple", "Du client au paiement",
];

const STEPS = [
  ["Comprendre", "On regarde comment le travail circule vraiment chez vous — une conversation, pas un audit de six semaines."],
  ["Simplifier", "On enlève les étapes qui ne servent plus personne : la ressaisie, les relances, les rapports montés à la main."],
  ["Connecter", "On relie les outils que vous avez déjà — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk."],
  ["Automatiser", "Le travail répétitif se fait tout seul, et chaque action attend le oui d'une personne."],
  ["Améliorer", "On mesure les heures récupérées, puis on passe au processus suivant — seulement sur preuve."],
];

const BEFORE = [
  "Les gens se souviennent de tout",
  "L'information vit dans les courriels",
  "Excel entre chaque étape",
  "Les suivis se font au téléphone",
  "Les chiffres sont ressaisis à la main",
  "Les délais se découvrent trop tard",
];

const AFTER = [
  "Le système s'en souvient",
  "Le client devient le projet, puis la facture",
  "L'équipe sait quoi faire aujourd'hui",
  "Le travail avance sans relances",
  "La facture part pendant que le travail est frais",
  "Ce qui demande votre attention est déjà sur une liste",
];

const FAQ = [
  {
    q: "Est-ce que je dois changer de logiciel ?",
    a: "Non. On connecte ce que vous utilisez déjà. Personne ne réapprend son travail — l'information circule enfin entre vos outils.",
  },
  {
    q: "Combien de temps avant un résultat ?",
    a: "Un premier processus connecté se mesure en semaines. Chez Construction Horizon : 43 % de réponses aux RFI plus rapides en six semaines, mesuré dans leurs dossiers.",
  },
  {
    q: "Ça coûte combien pour commencer ?",
    a: "Rien. La revue de 15 minutes est gratuite, et chaque projet commence par un audit d'analyse de processus gratuit (jusqu'à 2 rencontres). Ensuite, prix fixe : de 899 $ USD (≈ 1 250 $ CAD) à 4 779 $ USD (≈ 6 600 $ CAD), plus taxes selon l'envergure.",
  },
  {
    q: "Mes données restent où ?",
    a: "Chez vous, dans vos systèmes. Chaque changement attend votre accord, et tout est enregistré dans une piste d'audit. Conforme à l'esprit de la Loi 25.",
  },
];

function MiamiBannerFr() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("od-geo-tracked")) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/geo`)
      .then((r) => {
        sessionStorage.setItem("od-geo-tracked", "1");
        if (r.data.miami) setShow(true);
      })
      .catch(() => {});
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="miami-geo-banner-fr"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="bg-brand-navy text-white"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
            <p className="inline-flex items-center gap-2 text-sm font-medium">
              <MapPin size={14} className="text-brand-gold shrink-0" />
              Entreprises de Miami : redonnez de la capacité à votre équipe.
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/fr/miami"
                data-testid="miami-geo-banner-fr-cta"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white text-xs font-semibold px-4 py-2 transition-colors duration-300 hover:bg-brand-tealhover"
              >
                Voyez comment <ArrowRight size={13} />
              </Link>
              <button
                data-testid="miami-geo-banner-fr-close"
                onClick={() => setShow(false)}
                aria-label="Fermer"
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const FR_LINKS = [
  { label: "Miami : calculateur de capacité pour votre industrie", href: "/fr/miami" },
  { label: "Gardez vos outils. On les connecte.", href: "/fr/gardez-vos-outils" },
  { label: "Loi 25 et vos données clients", href: "/fr/loi-25-donnees-clients" },
  { label: "Réponse aux RFI en quelques heures", href: "/fr/reponse-rfi-quelques-heures" },
  { label: "Étude de cas : Construction Horizon (43 %)", href: "/fr/journal/horizon-construction-etude-de-cas" },
  { label: "L'audit gratuit de 15 minutes", href: "/fr/audit-gratuit-15-minutes" },
  { label: "Tarifs : un prix clair, une capacité mesurée", href: "/fr/tarifs" },
  { label: "Comment ça marche : vos 30 premiers jours", href: "/fr/comment-ca-marche" },
  { label: "Solutions : réparer, connecter, automatiser", href: "/fr/solutions" },
  { label: "À propos de Bruno Pouliot", href: "/fr/a-propos" },
  { label: "Démos en direct", href: "/fr/demos" },
];

export default function FrHomePage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Ocean Data — Plus de capacité. Meilleur travail. | Montréal";
    document.documentElement.lang = "fr";
    const links = [];
    const add = (hreflang, href) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    };
    add("fr-CA", `${SITE}/fr/`);
    add("es", `${SITE}/es/`);
    add("en-CA", `${SITE}/`);
    add("x-default", `${SITE}/`);
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/fr/`,
        name: "Ocean Data — Plus de capacité. Meilleur travail.",
        description:
          "Ocean Data connecte les outils que votre entreprise utilise déjà en un seul système simple, du client au paiement. Moins de paperasse, moins de courses, plus de contrôle. Revue gratuite de 15 minutes — Montréal et tout le Québec.",
        inLanguage: "fr-CA",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "FAQPage",
        inLanguage: "fr-CA",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="fr">
      <MiamiBannerFr />
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Ocean Data · Construction & services professionnels · Montréal
          </p>
          <h1
            data-testid="frhome-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="frhome-headline-line-1">Plus de capacité.</MaskedLine>
            <MaskedLine delay={0.3} testId="frhome-headline-line-2">
              <span className="text-brand-teal">Meilleur travail.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            On connecte le travail que votre équipe fait chaque jour — du
            client au paiement — en un seul système simple. Moins de paperasse.
            Moins de répétition. Moins de courses.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="frhome-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Montrez-moi où simplifier
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Revue gratuite de 15 minutes · Sans engagement</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-brand-goldsoft border border-brand-gold/30 px-5 py-2.5"
          >
            <span className="w-2 h-2 rounded-full bg-brand-gold" />
            <p className="text-xs sm:text-sm font-semibold text-brand-golddeep">
              25–40 % de temps administratif en moins · 647 implémentations depuis 2017
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-6 border-y border-slate-200/70 overflow-hidden bg-white" aria-hidden="true">
        <div className="od-marquee-track flex whitespace-nowrap">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-6 font-display font-semibold text-lg text-brand-navy/80 inline-flex items-center gap-6">
              {m} <span className="text-brand-gold">✳</span>
            </span>
          ))}
        </div>
      </div>

      {/* 5 étapes */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Comment ça marche
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Cinq étapes. Un processus à la fois.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map(([title, body], i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`frhome-step-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-6 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5">
                  <p className="font-mono text-sm text-brand-teal font-semibold mb-3">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-display font-semibold text-lg text-brand-navy mb-2.5">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Avant / Après */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Avant / Après
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Avant, les gens se souviennent. Après, le système s'en souvient.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            <Reveal>
              <div data-testid="frhome-before" className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 h-full">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold mb-6">Comment ça roule aujourd'hui</p>
                <ul className="space-y-4">
                  {BEFORE.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                      <X size={16} className="text-slate-400 shrink-0 mt-1" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="frhome-after" className="rounded-2xl bg-white border-2 border-brand-green/40 p-7 sm:p-8 h-full">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-green font-semibold mb-6">Avec Ocean Data</p>
                <ul className="space-y-4">
                  {AFTER.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                      <Check size={16} className="text-brand-green shrink-0 mt-1" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Preuve */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Des résultats mesurés, pas des promesses
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {[
              ["647", "implémentations Zoho One depuis 2017"],
              ["25–40 %", "de temps administratif en moins, mesuré chez nos clients"],
              ["43 %", "de réponses aux RFI plus rapides chez Construction Horizon"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`frhome-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <p className="font-display font-medium text-lg leading-snug text-white max-w-2xl">
                « En six semaines, le délai de réponse de notre équipe aux RFI a chuté de 43 %. Les décisions se prennent maintenant en heures, pas en jours. »
                <span className="block mt-2 text-xs text-slate-400 font-sans">Directeur des opérations, Construction Horizon</span>
              </p>
              <Link
                to="/fr/journal/horizon-construction-etude-de-cas"
                data-testid="frhome-case-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 text-brand-gold text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
              >
                Lire l'étude de cas <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Outils */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-8">
            Branché sur les outils que vous utilisez déjà
          </p>
          <p className="font-display font-semibold text-xl sm:text-2xl text-brand-navy/60 tracking-wide">
            Zoho One · Zoho CRM · Zoho Projects · Zoho Books · QuickBooks · Microsoft 365 · Google Workspace · Excel · Autodesk
          </p>
        </div>
      </section>

      <FilmSection lang="fr" />

      {/* Explorer en français */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Explorer en français
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-10">
            Par où commencer votre lecture
          </h2>
          <div className="space-y-3">
            {FR_LINKS.map((l, i) => (
              <Reveal key={l.href} delay={i * 0.05}>
                <Link
                  to={l.href}
                  data-testid={`frhome-link-${i}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl bg-white border border-slate-200 px-7 py-5 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="font-display font-semibold text-base sm:text-lg text-brand-navy group-hover:text-brand-teal transition-colors">
                    {l.label}
                  </span>
                  <ArrowRight size={17} className="text-brand-teal shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Questions fréquentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Ce qu'on nous demande avant de commencer
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`frhome-faq-${i}`} className="group rounded-2xl bg-[#F8FAFC] border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <span className="inline-flex w-11 h-11 rounded-full bg-white/10 text-brand-pale items-center justify-center mb-5">
                  <Waves size={18} />
                </span>
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Voyez les deux ou trois endroits où simplifier d'abord.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Une revue gratuite de 15 minutes. Une réponse personnelle en
                  un jour ouvrable. Rien à annuler.
                </p>
              </div>
              <button
                data-testid="frhome-cta-final"
                onClick={() => setOpen(true)}
                className="shrink-0 inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                Montrez-moi où simplifier
                <ArrowRight size={18} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
